import { useEffect, useMemo, useState } from 'react';
import { Button, Header, Icon, Message, Segment, Table } from 'semantic-ui-react';
import { describe } from '../approvals.js';
import { MONTH_NAMES, monthOfAsAt } from '../periods.js';
import { buildSummary, fmt, latestPerReport } from '../summary.js';

/* Every report for a chosen year and month, side by side, with each field summed over the report's sites and across all reports.
 * Open to everyone, whoever the account manager is. */
export default function SummaryTab({ reports, approvals, loadFull, onExcel, busy }) {
  const years = useMemo(() => [...new Set(reports.map((r) => r.report_year))].sort((a, b) => b - a), [reports]);
  const [year, setYear] = useState(null);
  const [month, setMonth] = useState(null);
  const [entries, setEntries] = useState(null);                    // [{ report, figures }] for the chosen year
  const [error, setError] = useState(null);

  const y = year ?? years[0] ?? null;
  const picked = useMemo(() => (y === null ? [] : latestPerReport(reports, y)), [reports, y]);
  const m = month ?? (picked.length ? Math.max(...picked.map((r) => monthOfAsAt(r.as_at_date))) : 0);

  useEffect(() => {
    let live = true;
    setEntries(null); setError(null);
    Promise.all(picked.map((r) => loadFull(r))).then((all) => { if (live) setEntries(all); })
      .catch((e) => { if (live) setError(e.message); });
    return () => { live = false; };
  }, [picked, loadFull]);

  const summary = useMemo(() => (entries && y !== null ? buildSummary(entries, y, m, approvals) : null), [entries, y, m, approvals]);

  return (
    <section aria-labelledby="sum-heading">
      <Header as="h2" id="sum-heading" className="titlebar">
        <Icon name="table" />
        <Header.Content>Summary<Header.Subheader>All reports for a month, each field summed over the locations</Header.Subheader></Header.Content>
      </Header>
      <div className="toolbar" role="group" aria-label="Choose the summary period">
        <label htmlFor="sum-year">Year</label>
        <select id="sum-year" className="ui dropdown" value={y ?? ''} onChange={(e) => { setYear(Number(e.target.value)); setMonth(null); }}>
          {years.map((v) => <option key={v} value={v}>{v}</option>)}
        </select>
        <label htmlFor="sum-month">Month</label>
        <select id="sum-month" className="ui dropdown" value={m} onChange={(e) => setMonth(Number(e.target.value))}>
          {MONTH_NAMES.map((n, i) => <option key={n} value={i}>{n}</option>)}
        </select>
        <Button type="button" icon="file excel outline" labelPosition="left" content="Download to Excel" loading={busy === 'summary'}
          disabled={!summary || !!busy} onClick={() => onExcel(summary)} />
      </div>
      {error && <Message negative role="alert">{error}</Message>}
      {!error && !summary && y !== null && <Message info role="status">Loading reports…</Message>}
      {y === null && <Segment placeholder><Header icon><Icon name="folder open outline" />There are no reports yet.</Header></Segment>}
      {summary && summary.columns.length === 0 && <Message info>No reports for {y}.</Message>}
      {summary && summary.columns.length > 0 && (
        <div className="gridwrap" tabIndex={0} role="region" aria-label={`${summary.title} (scrollable)`}>
          <Table celled compact unstackable className="datagrid sumtable">
            <caption className="visually-hidden">{summary.title}. One column per report and an All reports column; by-site figures are summed over the sites.</caption>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell scope="col" className="lab">{summary.month} {summary.year}</Table.HeaderCell>
                {summary.columns.map((c) => (
                  <Table.HeaderCell scope="col" key={c.key} textAlign="center">{c.id}<div className="sub">{c.client}</div></Table.HeaderCell>
                ))}
                <Table.HeaderCell scope="col" textAlign="center">All reports</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              <Table.Row>
                <Table.HeaderCell scope="row" className="lab">Account manager</Table.HeaderCell>
                {summary.columns.map((c) => <Table.Cell key={c.key} textAlign="center">{c.manager || '–'}</Table.Cell>)}
                <Table.Cell />
              </Table.Row>
              {[['finance', 'Finance approval'], ['account_manager', 'AM approval']].map(([k, label]) => (
                <Table.Row key={k}>
                  <Table.HeaderCell scope="row" className="lab">{label}</Table.HeaderCell>
                  {summary.columns.map((c) => <Table.Cell key={c.key} textAlign="center" className="approvalcell">{describe(c[k]) || <span className="hint">Pending</span>}</Table.Cell>)}
                  <Table.Cell />
                </Table.Row>
              ))}
              {summary.sections.map((sec) => (
                <SectionRows key={sec.section} sec={sec} cols={summary.columns.length} />
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </section>
  );
}

function SectionRows({ sec, cols }) {
  return (
    <>
      <Table.Row className="sumsec"><Table.HeaderCell scope="colgroup" colSpan={cols + 2}>{sec.title}</Table.HeaderCell></Table.Row>
      {sec.rows.map((r) => (
        <Table.Row key={r.key}>
          <Table.HeaderCell scope="row" className="lab" title={r.label}>{r.label}</Table.HeaderCell>
          {r.values.map((v, i) => <Table.Cell key={i} textAlign="right">{fmt(r.kind, v) || '–'}</Table.Cell>)}
          <Table.Cell textAlign="right"><strong>{fmt(r.kind, r.total) || '–'}</strong></Table.Cell>
        </Table.Row>
      ))}
    </>
  );
}
